<template>
  <div class="check-in-report relative text-lg mb-2">
    <div v-if="error" class="error text-center">
      {{ error }}
    </div>
    <h1 class="page-header text-center text-3xl">check-in report</h1>
    <div class="text-center">
      analyze the manual check-ins provided by your staff
    </div>
    <div class="flex justify-center items-center absolute top-2 right-5">
      <div v-if="downloadReportLoading" class="flex justify-center items-center w-40 mt-3">
        <loading-icon />
      </div>
      <template v-else>
        <!--
          We need "download" attribute here to download file on click
          instead of instead of navigating to the file.
          Please check for more info: https://www.w3schools.com/tags/att_a_download.asp
        -->
        <a v-if="downloadUrl" :href="downloadUrl" class="button" download>
          download report
        </a>
        <button
          v-else
          :class="{ disabled: !reportId }"
          class="button"
          @click="generateExport"
        >
          download report
        </button>
      </template>
    </div>
    <DateRange
        :start.sync="startDate"
        :end.sync="endDate"
        :max="maxDate"
        :min="startDate"
        format="MM/dd/yyyy"
        @update:range="updateRange"
        class="w-56 mx-auto p-0 font-primary text-md"
    />
    <button
      class="button w-28 mx-auto"
      @click="runReport"
    >
      run report
    </button>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
        <loading-icon />
    </div>
    <div v-else class="mt-12">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        disableTableSort
      />
      <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
            :page.sync="page"
            :pages="pagesTotal"
            :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { format, subDays } from 'date-fns';
import { utcToZonedTime } from "date-fns-tz";
import { mapState } from "vuex";
import { DateRange, SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { ReportAPI } from '../../services/api/Reports';

export default {
  name: 'CheckInReport',
  components: {
    DateRange,
    SbCustomTable,
    LoadingIcon,
    Pagination,
  },
  data() {
    return {
      startDate: new Date(subDays(new Date(), 89)),
      endDate: new Date(),
      maxDate: new Date(),
      downloadUrl: "",
      page: 0,
      pagesTotal: 1,
      reportId: null,
      tableRows: [],
      loadingReport: false,
      downloadReportLoading: false,
      tableColumns: [],
      error: null,
    }
  },
  async created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT');
  },
  watch: {
    page(value) {
      this.fetchReportData(value + 1);
    }
  },
  computed: {
    ...mapState(['currentMerchant'])
  },
  methods: {
    handleError(error) {
      this.error = error;
    },
    async fetchReportData(page) {
      this.loadingReport = true;
      try {
        const reportRes = await ReportAPI.getReportById(this.reportId, page);
        const { results } = reportRes.data.report.output_data;
        this.pagesTotal = reportRes.data.pagination.total_pages;
        if (results.length > 0) {
          const columns = Object.keys(results[0]).map(key => ({
            key,
            value: key.replace('_', ' '),
          }));
          this.tableColumns = columns;
          this.tableRows = results.map(result => ({
            ...result,
            visit_date: format(utcToZonedTime(new Date(result.visit_date), this.currentMerchant.merchant_time_zone), 'MM/dd/yyyy, hh:mmaaa'),
          }));
        }
      } catch(error) {
        this.handleError(error);
      }
      this.loadingReport = false;
    },
    async generateReport(startDate, endDate) {
      this.error = null;
      this.loadingReport = true;
      this.page = 0;
      try {
        const res = await ReportAPI.createReport(
          'Reports::CheckInReport',
          startDate,
          endDate,
        );
        this.reportId = res.data.report?.id;
      } catch (error) {
        this.handleError(error);
      }
      this.loadingReport = false;
    },
    async checkDownloadUrlStatus() {
      try {
        const res = await ReportAPI.getReportDownloadUrl(this.reportId);
        if (res.data.report.download_url) {
          this.downloadUrl = res.data.report.download_url;
          this.downloadReportLoading = false;

          // Download file right after generation is done
          const link = document.createElement('a');
          link.href = this.downloadUrl;
          link.download = true;
          link.click();
          link.remove();
        }
        else if(res.data.report.export_state === 'failed') {
          this.handleError(new Error('report generation failed. please try again.'));
          this.downloadReportLoading = false;
        }
        else {
          setTimeout(this.checkDownloadUrlStatus, 1000);
        }
      }
      catch(error) {
        this.handleError(error);
        this.downloadReportLoading = false;
      }
    },
    generateExport() {
      this.error = null;
      if (!this.downloadUrl) {
        this.downloadReportLoading = true;
        ReportAPI.generateExportByReportId(this.reportId);
        setTimeout(this.checkDownloadUrlStatus, 1000);
      }
    },
    async runReport() {
      if (this.startDate && this.endDate) {
        await this.generateReport(this.startDate, this.endDate);
        await this.fetchReportData(1);
      }
      else {
        this.error = 'please select a start and end date';
      }
    },
    async updateRange(value) {
      this.startDate = value.start;
      this.endDate = value.end;
      if (value.start && value.end) {
        try {
          await this.generateReport(value.start, value.end);
          await this.fetchReportData(this.page + 1);
        }
        catch(err) {
          this.handleError(err);
        }
      }
      else {
        this.error = 'please select a start and end date';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.check-in-report {
  .page-header {
    margin: 0;
  }
  .button {
    display: block;
    margin: 10px auto 0 auto;
    height: 30px;
    font-size: 1rem;
    padding: 5px 20px;
    color: rgba(255, 255, 255, 0.7);
    border-radius: 5px;
  }
  .sb-input {
    padding: 0;
    color: #007aff;
  }
}

</style>
